import React from "react";
import { Chip, Tooltip } from "@heroui/react";
import { Typography } from "../../../../components";
import { OnlyFansAccount } from "../../../../api/staff";
import { InfoCircle, BlueCircleIc } from "../../../../components/icons";
import { useTranslations } from "../../../../contexts/translation.context";
import { formatTimeAgo } from "../../../../utils/formatTimeAgo";
import IcDate from '../../../../components/ic-date.svg';

interface CampaignsCellProps {
  account: OnlyFansAccount;
}

/**
 * Helper function to get the correct plural form for "campaigns"
 * Based on Russian language pluralization rules
 */
const getCampaignsText = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'кампаний';
  }

  if (lastDigit === 1) {
    return 'кампания';
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'кампании';
  }

  return 'кампаний';
};

/**
 * CampaignsCell Component
 *
 * Renders the RevShare campaigns information cell with:
 * - Offer status chip (active/moderation/no offers)
 * - Commission percentage with tooltip
 * - Last update timestamp
 */
const CampaignsCell: React.FC<CampaignsCellProps> = ({ account }) => {
  const { t } = useTranslations();
  const offer = account.offer;

  // If no offer, show "Нет офферов"
  if (!offer) {
    return (
      <Chip
        className="h-[24px] px-[8px] text-[12px] font-medium"
        style={{ backgroundColor: '#F4F4F5', color: '#71717A' }}
      >
        <div className="flex items-center gap-[4px]">
          <div className="w-2 h-2 bg-[#71717A] rounded-full"></div>
          <span>{t('accounts.noOffers') || 'Нет офферов'}</span>
        </div>
      </Chip>
    );
  }

  // Offer exists - show status, commission and last update
  const offerStatus = offer.status?.toLowerCase();
  const statusConfig = offerStatus === 'active'
    ? { dot: '#17C964', text: t('accounts.offerActive') || 'Активен', chipBg: '#F4F4F5', chipText: '#11181C' }
    : offerStatus === 'moderation'
      ? { dot: '#F5A524', text: t('accounts.offerModeration') || 'На модерации', chipBg: '#FFF4E5', chipText: '#F5A524' }
      : { dot: '#71717A', text: offer.status || t('accounts.noOffers') || 'Нет офферов', chipBg: '#F4F4F5', chipText: '#71717A' };

  const activeCount = account.ref_share_orders_active_count ?? 0;
  const totalCount = account.ref_share_orders_total_count ?? 0;

  return (
    <div className="flex flex-col gap-[4px]">
      <div className="flex gap-[8px]">
        {/* Status chip */}
        <Chip
          className="h-[24px] px-[8px] text-[12px] font-medium"
          style={{ backgroundColor: statusConfig.chipBg, color: statusConfig.chipText }}
        >
          <div className="flex items-center gap-[4px]">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: statusConfig.dot }}
            ></div>
            <span>{statusConfig.text}</span>
          </div>
        </Chip>

        {/* Commission with tooltip */}
        <div className="flex items-center gap-[2px]">
          <Typography variant="body3" className="text-[#71717A] text-[12px]">
            {offer.commission}%
          </Typography>
          <Tooltip
            content={t('accounts.commissionTooltip') || 'Комиссия маркетолога + комиссия OnlyU'}
            placement="top"
            classNames={{
              content: "bg-white border border-[#EDEDED] rounded-[8px] px-[12px] py-[8px] text-[12px] text-[#11181C] shadow-sm",
            }}
          >
            <div className="cursor-pointer">
              <InfoCircle />
            </div>
          </Tooltip>
        </div>
      </div>

      {/* RevShare campaigns count */}
      <div className="flex items-center gap-[4px]">
        <Typography variant="body3" className="!text-[#006FEE] text-[12px] font-regular leading-[18px]">
          {activeCount}
        </Typography>
        <Typography variant="body3" className="!text-[#D4D4D8] text-[12px] font-regular leading-[18px]">
          •
        </Typography>
        <Typography variant="body3" className="!text-[#71717A] text-[12px] font-regular leading-[18px]">
          {totalCount} {getCampaignsText(totalCount)}
        </Typography>
      </div>

      {/* Last update */}
      {account.granted?.updated_at && (
        <Tooltip
          content={
            <div className="text-[12px] flex flex-col gap-[4px]">
              <Typography variant="body3" className="!text-[#71717A]">{t('accounts.lastUpdateTooltip') || 'Последняя синхронизация данных'}</Typography>
              <Typography variant="body2">{new Date(account.granted.updated_at).toLocaleString()}</Typography>
            </div>
          }
          placement="top"
          classNames={{
            content: "bg-white border border-[#EDEDED] rounded-[8px] px-[12px] py-[8px] shadow-sm",
          }}
        >
          <div className="flex items-center gap-[4px] cursor-pointer">
            <img src={IcDate} alt="Date" className="w-[14px] h-[14px]" />
            <Typography variant="body3" className="text-[#A1A1AA] text-[10px]">
              {formatTimeAgo(account.granted.updated_at, t)}
            </Typography>
            <BlueCircleIc />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default CampaignsCell;
